<template>
  <span class="rmh-wrap">
    <b-button size="sm" variant="outline-secondary"
              title="How to use the Risk Matrix" aria-label="Help" @click="open()">
      <i class="fa fa-info-circle"></i>
    </b-button>

    <b-modal v-model="show_help" title="Using the Risk Matrix" size="lg" ok-only scrollable>
      <div class="rmh-body">

        <h4>What this page answers</h4>
        <p>
          Not just <i>who scores well</i>, but <b>how many miles each driver's score actually
          rides on</b>. Every scorecard on the roster can look acceptable and the company can
          still be carrying more risk than it needs to &mdash; because <b>the schedule</b>, not
          the scoring, decides how much road each score covers.
        </p>

        <hr/>

        <h4>The one thing to read first</h4>
        <p>
          The first card compares two things that sound identical and are not. Here is the whole
          idea in five drivers:
        </p>

        <div class="rmh-fig">
          <div class="rmh-fig-row">
            <div class="rmh-fig-label">
              <b>The average driver</b>
              <span>one person, one vote</span>
            </div>
            <div class="rmh-people">
              <span v-for="n in 4" :key="'g' + n" class="rmh-dot" :style="{ background: cGood }"></span>
              <span class="rmh-dot" :style="{ background: cBad }"></span>
            </div>
            <div class="rmh-fig-verdict rmh-good">4 of 5 score well &mdash; looks fine</div>
          </div>

          <div class="rmh-fig-row">
            <div class="rmh-fig-label">
              <b>The average mile</b>
              <span>each person sized by how far they drive</span>
            </div>
            <div class="rmh-people">
              <span v-for="n in 4" :key="'gs' + n" class="rmh-dot rmh-dot--sm" :style="{ background: cGood }"></span>
              <span class="rmh-dot rmh-dot--xl" :style="{ background: cBad }"></span>
            </div>
            <div class="rmh-fig-verdict rmh-bad">most miles belong to the weak one</div>
          </div>
        </div>

        <p>
          Same five people, two different answers. If the average <i>mile</i> is driven by a
          <b>lower</b> score than the average <i>driver</i> has, your busier people are your
          weaker people.
        </p>

        <div class="rmh-call rmh-call--good">
          <b>&ldquo;Stronger drivers&rdquo;</b> &mdash; your busier drivers have the better
          scores. The schedule is working in your favour.
        </div>
        <div class="rmh-call rmh-call--bad">
          <b>&ldquo;Weaker drivers&rdquo;</b> &mdash; your busier drivers have the worse scores.
          Worth a scheduling conversation. No individual scorecard shows this, because it belongs
          to the schedule rather than to any one driver &mdash; which is also why it is fixable
          without anyone changing how they drive.
        </div>

        <hr/>

        <h4>&ldquo;Your riskiest drivers&rdquo; and the 25% mark</h4>
        <p>
          The second card takes the quarter of your roster with the lowest combined scores and
          asks one question: <b>how much of the driving do they do?</b>
        </p>
        <p>
          The 25% comparison is not a target anyone set &mdash; it falls out of the arithmetic.
          They are <b>a quarter of the drivers</b>, so if every driver covered the same distance
          they would cover <b>a quarter of the miles</b>. That is the neutral mark.
        </p>
        <div class="rmh-call rmh-call--good">
          <b>Below 25%</b> &mdash; your weakest scores are doing less than their share of the
          driving. The heavy mileage is going to better drivers.
        </div>
        <div class="rmh-call rmh-call--bad">
          <b>Above 25%</b> &mdash; your weakest scores are doing more than their share. The heavy
          mileage is landing on exactly the people you would least want it to.
        </div>
        <p class="rmh-mut rmh-small">
          The card stays in a caution tone either way. The group is your weakest scores whatever
          the percentage says, so it is never quite an all-clear.
        </p>

        <hr/>

        <h4>The map</h4>
        <p>
          Every driver is one dot. Left to right is their <b>combined score</b> &mdash; the same
          score the tier chips and the roster ranking use, so a driver sits in the same place here
          as everywhere else in Driver Manager. Bottom to top is <b>how far they drove</b>. Every
          dot is the same size: both axes are already spent, and sizing them by something else
          would be decoration rather than information.
        </p>

        <div class="rmh-map">
          <div class="rmh-map-y">Miles driven &uarr;</div>
          <div class="rmh-grid">
            <div v-for="cell in quadCells" :key="cell.corner" class="rmh-cell" :style="{ '--c': cell.color }">
              <div class="rmh-cell-name">{{ cell.label }}</div>
              <div class="rmh-cell-note">{{ cell.note }}</div>
            </div>
          </div>
          <div class="rmh-map-x">Combined score &rarr; <span class="rmh-mut">(better to the right)</span></div>
        </div>

        <p class="rmh-mut rmh-small">
          The dashed lines are the fleet's own averages, so the quadrants move as the fleet does.
          Click any dot &mdash; or any name &mdash; to open that driver.
        </p>

        <hr/>

        <h4>&ldquo;Miles their record supports&rdquo;</h4>
        <p>
          The mark is <b>that driver's own mileage, scaled by their score</b>. Score exactly the
          fleet average and the mark sits on the end of your bar. Score above it and the mark moves
          right &mdash; you could carry more. Score below and it moves left.
        </p>

        <div class="rmh-fig">
          <div class="rmh-bar-row">
            <div class="rmh-bar-who">Runs long, low score</div>
            <div class="rmh-bar-track">
              <div class="rmh-bar-fill" :style="{ width: '78%', background: cBad }"></div>
              <div class="rmh-bar-mark" style="left: 42%"></div>
            </div>
            <div class="rmh-bar-tag rmh-bad">over-dispatched</div>
          </div>
          <div class="rmh-bar-row">
            <div class="rmh-bar-who">Runs long, high score</div>
            <div class="rmh-bar-track">
              <div class="rmh-bar-fill" :style="{ width: '78%', background: cGood }"></div>
              <div class="rmh-bar-mark" style="left: 62%"></div>
            </div>
            <div class="rmh-bar-tag rmh-mut">fine &mdash; that is dispatch working</div>
          </div>
          <div class="rmh-bar-row">
            <div class="rmh-bar-who">Runs short, high score</div>
            <div class="rmh-bar-track">
              <div class="rmh-bar-fill" :style="{ width: '26%', background: cRoom }"></div>
              <div class="rmh-bar-mark" style="left: 58%"></div>
            </div>
            <div class="rmh-bar-tag rmh-room">room to give</div>
          </div>
        </div>

        <p>
          Because the mark is scaled off each driver's own mileage, a <b>strong</b> driver's mark
          always sits to the right of their bar however far they drive &mdash; which is the point.
          Only a below-average score can put the mark behind the bar, so being past it and being
          <b>over-dispatched</b> are the same thing.
        </p>

        <hr/>

        <h4>What this page will not tell you</h4>
        <p>
          <b>Nothing from the Event Response Manager.</b> This page runs on the scorecard alone
          &mdash; combined score and miles. An earlier version measured risk from logged ERM
          events instead; that machinery still exists and can be put back, but nothing on this
          page depends on it today.
        </p>
        <p>
          <b>No event counts, no dollar figures, no predictions.</b> Every number here is either a
          scorecard figure or simple arithmetic on two of them.
        </p>

        <hr/>

        <h4>Why some drivers are missing</h4>
        <p>
          A driver needs a combined score and some mileage to appear. Anyone without a score is
          left off rather than shown at zero, which would park them in the worst corner on no
          evidence at all. <b>Insufficient Data</b> drivers &mdash; too few miles or bus days to
          score fairly &mdash; are excluded for the same reason.
        </p>
        <p>
          The score axis runs a fixed 0&ndash;100, so no single driver can stretch it or squash
          everyone else against one edge.
        </p>
      </div>
    </b-modal>
  </span>
</template>

<script>
import {
  exposureQuadrantColor,
  EXPOSURE_CORNER_QUADRANTS,
  EXPOSURE_CORNER_LABELS,
  EXPOSURE_OVER_RISK,
  EXPOSURE_WORKHORSE,
  EXPOSURE_LOW_LOW,
  EXPOSURE_UNDERUSED,
} from '../utils/risk_matrix'

// the diagram's caption per quadrant -- blunter and shorter than the blurbs on
// the page itself, which have room to explain
const CELL_NOTE = {
  [EXPOSURE_OVER_RISK]: 'Low score, lots of miles. Fix this first.',
  [EXPOSURE_WORKHORSE]: 'High score, lots of miles. Leave them be.',
  [EXPOSURE_UNDERUSED]: 'Low score, few miles. Coach, do not reschedule.',
  [EXPOSURE_LOW_LOW]: 'High score, few miles. Room on the calendar.',
}

// screen order for the 2x2 grid: top row left to right, then bottom row
const CELL_ORDER = ['topLeft', 'topRight', 'bottomLeft', 'bottomRight']

export default {
  name: 'driver_manager_risk_matrix_help',

  data() {
    return { show_help: false, midnight: false }
  },

  computed: {
    // built from the SAME maps the chart draws with, so the diagram can never
    // describe a layout the chart does not have
    quadCells() {
      return CELL_ORDER.map(corner => {
        const quadrant = EXPOSURE_CORNER_QUADRANTS[corner]
        return {
          corner,
          label: EXPOSURE_CORNER_LABELS[corner],
          note: CELL_NOTE[quadrant],
          color: exposureQuadrantColor(quadrant, this.midnight),
        }
      })
    },
    cBad() { return exposureQuadrantColor(EXPOSURE_OVER_RISK, this.midnight) },
    cGood() { return exposureQuadrantColor(EXPOSURE_WORKHORSE, this.midnight) },
    cRoom() { return exposureQuadrantColor(EXPOSURE_LOW_LOW, this.midnight) },
  },

  methods: {
    open() {
      // resolved on open, not in created(): the site theme can change under a
      // mounted component and this only matters while the modal is showing
      this.midnight = document.documentElement.getAttribute('data-fi-theme') === 'midnight'
      this.show_help = true
    },
  },
}
</script>

<style scoped>
.rmh-body h4 { font-size: 1.05rem; margin-top: 4px; }
.rmh-body p, .rmh-body li { font-size: 0.95rem; }
.rmh-mut { color: var(--fi-text-muted, #6c757d); }
.rmh-small { font-size: 0.85rem; }

.rmh-fig {
  background: var(--fi-surface-sunken, #f1f5f9);
  border: 1px solid var(--fi-line, #dee2e6);
  border-radius: 6px;
  padding: 14px 16px;
  margin: 14px 0;
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.rmh-fig-row {
  display: grid;
  grid-template-columns: 190px 1fr auto;
  align-items: center;
  gap: 14px;
}
.rmh-fig-label { display: flex; flex-direction: column; line-height: 1.3; }
.rmh-fig-label b { font-size: 0.9rem; }
.rmh-fig-label span { font-size: 0.75rem; color: var(--fi-text-muted, #6c757d); }
.rmh-people { display: flex; align-items: center; gap: 9px; }
.rmh-dot { width: 20px; height: 20px; border-radius: 50%; display: inline-block; flex: none; }
.rmh-dot--sm { width: 11px; height: 11px; }
.rmh-dot--xl { width: 40px; height: 40px; }
.rmh-fig-verdict { font-size: 0.8rem; font-weight: 600; }
.rmh-good { color: #1E7B3C; }
.rmh-bad { color: #C1352F; }
.rmh-room { color: #2F6690; }

.rmh-call {
  border-left: 3px solid var(--fi-line, #dee2e6);
  padding: 9px 13px;
  margin: 9px 0;
  font-size: 0.9rem;
  border-radius: 0 4px 4px 0;
}
.rmh-call--good { border-left-color: #1E7B3C; background: rgba(30, 123, 60, 0.08); }
.rmh-call--bad { border-left-color: #C1352F; background: rgba(193, 53, 47, 0.08); }

.rmh-map {
  display: grid;
  grid-template-columns: 22px 1fr;
  grid-template-rows: 1fr 22px;
  gap: 6px;
  margin: 14px 0;
}
.rmh-map-y {
  grid-column: 1; grid-row: 1;
  writing-mode: vertical-rl;
  transform: rotate(180deg);
  text-align: center;
  font-size: 0.72rem; font-weight: 600;
  color: var(--fi-text-muted, #6c757d);
}
.rmh-map-x {
  grid-column: 2; grid-row: 2;
  text-align: center;
  font-size: 0.72rem; font-weight: 600;
  color: var(--fi-text-muted, #6c757d);
}
.rmh-grid {
  grid-column: 2; grid-row: 1;
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 8px;
}
.rmh-cell {
  border: 1.5px solid var(--c);
  border-radius: 5px;
  background: var(--fi-surface, #fff);
  padding: 11px 12px;
  min-height: 76px;
  /* the hue reads as a wash behind the text rather than a block of colour,
     which keeps the label legible in either theme */
  box-shadow: inset 0 0 0 999px color-mix(in srgb, var(--c) 9%, transparent);
}
.rmh-cell-name {
  font-size: 0.78rem; font-weight: 700; letter-spacing: 0.03em;
  color: var(--c); margin-bottom: 3px;
}
.rmh-cell-note { font-size: 0.8rem; color: var(--fi-text, #212529); }

.rmh-bar-row {
  display: grid;
  grid-template-columns: 190px 1fr auto;
  align-items: center;
  gap: 14px;
}
.rmh-bar-who { font-size: 0.85rem; }
.rmh-bar-track {
  position: relative;
  height: 16px;
  background: var(--fi-surface, #fff);
  border: 1px solid var(--fi-line, #dee2e6);
  border-radius: 3px;
}
.rmh-bar-fill { position: absolute; left: 0; top: 0; bottom: 0; border-radius: 2px; opacity: 0.9; }
.rmh-bar-mark { position: absolute; top: -3px; height: 22px; width: 2px; background: var(--fi-text, #1a2733); }
.rmh-bar-tag { font-size: 0.78rem; font-weight: 600; }

@media (max-width: 575px) {
  .rmh-fig-row, .rmh-bar-row { grid-template-columns: 1fr; gap: 6px; }
  .rmh-grid { grid-template-columns: 1fr; }
}
</style>
